import findIndex from 'lodash/findIndex';
import setCustomStyles from '_shared_/js/utils/setCustomStyles';
import getUrlParams from '_shared_/js/utils/getUrlParams';
import getBaseUrl from '_shared_/js/utils/getBaseUrl';
import highlightModel from './highlightModel.js';
import secondToMMSS from '_shared_/js/utils/secondToMMSS.js';
import * as Sentry from '@sentry/browser';
import { applyTestDataToStore, getTestData } from './TestState/testDataLoader.js';
import { HostBridge, contentAPI, isHostCommunicationInitialized, sessionAPI } from './communication';

class LoadingProcess {
    constructor(eventBus, store) {
        /**
         * This is only used in local environements, where
         * we need to support both loading standalone
         * and inside an iframe from farfalla or tartaruga.
         * For production or staging, we only load the reader
         * from inside an iframe.
         * We might need to tweak this for the new fenice app.
         */
        this.insideIframe = window.self !== window.top;
        this.eventBus = eventBus;
        // We use $store to be within Vue's standards
        this.$store = store;
    }

    init() {
        this.eventBus.$emit('LoadingProcess.init');
        if (this.$store.state.isLoadingProcessFinished) return;
        if (document.readyState === 'complete') {
            this.determineEnvironment();
        } else {
            window.addEventListener(
                'load',
                _e => {
                    this.determineEnvironment();
                },
                false
            );
        }
    }

    determineEnvironment() {
        this.eventBus.$emit('LoadingProcess.determineEnvironment');
        this.determineFileType();

        // E2E tests force standalone mode to bypass Delfino/iframe checks
        if (window.__E2E_STANDALONE_MODE__) {
            this.loadStandAloneDevMode();
            return;
        }

        /**
         * In production or staging, the reader will only work from inside an iframe.
         * This is true for both farfalla and tartaruga.
         * This way we avoid showing our dev/debug data to the world.
         */
        if (import.meta.env.VITE_ENV === 'local') {
            this.insideIframe ? this.loadInsideIframe() : this.loadStandAloneDevMode();
        } else {
            this.loadInsideIframe();
        }
    }

    /**
     * This is only a helper to ease local development.
     * With this, we can control which kind of reader
     * it's going to boot using a ?file_type=xxx
     * query param. Options are pdf, epub and audio
     */
    determineFileType() {
        this.eventBus.$emit('LoadingProcess.determineFileType');
        if (window.location.search.split('?')[1]) {
            const params = window.location.search.split('?')[1].split('&');
            const fileType = params
                .filter(param => {
                    return param.split('=')[0] == 'file_type';
                })
                .pop();

            if (fileType && ['pdf', 'epub', 'audio'].includes(fileType.split('=')[1])) {
                this.$store.commit('setFileType', {
                    fileType: fileType.split('=')[1],
                });
            }
        }
    }

    // ============================================================
    // DELFINO MODE - New Architecture
    // ============================================================

    /**
     * Load via Delfino bridge (new architecture).
     * Uses session.initialize() and content.getContentStructure().
     */
    async loadViaDelfino() {
        this.eventBus.$emit('LoadingProcess.loadViaDelfino');

        const initData = await sessionAPI.initialize();
        this.storeSessionInitData(initData);

        const content = await contentAPI.getContentStructure();
        this.storeContentStructure(content);

        this.navigateToLastLocation(initData);

        this.$store.dispatch('openOnboarding');
        this.$store.dispatch('initializeThemeListener');
        this.$store.commit('polpoSessionHasBeenInitiated');
        this.$store.commit('setHighlightModel', highlightModel);
        this.$store.dispatch('fetchSelections');
        this.$store.dispatch('fetchAiActions');

        this.setSentryTagsFromInitData(initData);
        this.loadingProcessFinished();
    }

    /**
     * Store session initialization data from Delfino.
     *
     * @param {SessionInitData} initData - Data from session.initialize()
     */
    storeSessionInitData(initData) {
        this.$store.commit('parentUrl', { url: initData.tenant.hostLocation });

        // Modes
        this.$store.commit('previewMode', { previewMode: initData.modes.preview });
        this.$store.commit('embeddedMode', { embeddedMode: initData.modes.embedded });
        this.$store.commit('setExhibitionMode', { exhibitionMode: initData.modes.exhibition });

        // User
        this.$store.commit('setUserIsGuest', { userIsGuest: initData.user.isGuest });
        this.$store.commit('anonymousSession', { anonymous_session: initData.user.isAnonymous });

        // Tenant
        this.$store.commit('setTenantId', { tenantId: initData.tenant.id });
        this.$store.commit('lang', { lang: initData.tenant.lang });
        if (initData.tenant.customStyles) {
            setCustomStyles(initData.tenant.customStyles);
        }

        // Issue
        this.$store.commit('setIssueId', { issue_id: initData.issue.id });
        this.$store.commit('setFileType', { fileType: initData.issue.fileType });
        this.$store.commit('title', { title: initData.issue.name });
        this.$store.commit('publicationAbout', { publication_about: initData.issue.description });
        this.$store.commit('descriptionRawText', {
            description_raw_text: initData.issue.descriptionRawText,
        });
        this.$store.commit('tags', { tags: initData.issue.tags });
        this.$store.commit('publicationDate', { publication_date: initData.issue.publicationDate });
        this.$store.commit('publicationPlace', { publicationPlace: initData.issue.publicationPlace ?? '' });
        this.$store.commit('issueUrl', { issue_url: getBaseUrl(initData.issue.issueFullUrl) });
        this.$store.commit('setIssueLang', { lang: initData.issue.lang });
        this.$store.commit('shareUrl', { share_url: initData.issue.shareUrl });
        this.$store.commit('setTwitterUser', { twitterUser: initData.issue.twitterUser });
        this.$store.commit('setIsBuyable', { isBuyable: initData.issue.isBuyable });
        this.$store.commit('setIsBuyableButtonText', {
            isBuyableButtonText: initData.issue.buyableButtonText,
        });
        this.$store.commit('setComponentListsIframeUrl', {
            iframeUrl: initData.issue.listsIframeUrl,
        });

        // Handle shared article from URL
        if (initData.issue.issueFullUrl) {
            this.$store.commit('setSharedArticle', {
                articleId: getUrlParams(initData.issue.issueFullUrl, 'article_id'),
                showed: false,
            });
        }

        // Settings
        this.$store.commit('setSettings', initData.settings.reader);
        this.$store.commit('setPromoteAppData', initData.settings.ui.promoteApp);
        this.$store.dispatch('setUserSettings', initData.settings.user);
        this.$store.dispatch('initToggleUi', { uiHidden: initData.settings.ui.hiddenElements });

        // Features
        this.$store.commit('setListeningEnabled', { tts_enabled: initData.features.tts.enabled });
        this.$store.commit('setListeningDriver', { tts_driver: initData.features.tts.driver });
        this.$store.commit('setExtraVoices', { chinese: initData.features.chineseVoice });
        this.$store.commit('setSearchEnabled', { search_enabled: initData.features.search });
        this.$store.commit('setNotesEnabled', { notes_enabled: initData.features.notes });
        this.$store.commit('setAiIntegrationsEnabledOrDisabled', {
            enabled: initData.features.aiIntegrations,
        });
        this.$store.commit('setEnforceConcurrencyLimit', initData.features.concurrencyLimit);
        this.$store.commit('setCopyContentToClipboard', { enabled: initData.features.copyEnabled });
        this.$store.commit('setReaderPluginSrc', { src: initData.features.readerPlugin });

        // Manifest version
        this.$store.commit('plaPublicationManifestVersion', {
            plaPublicationManifestVersion: initData.manifestVersion ?? '1.0.0',
        });
        this.$store.commit('contentEncryptionIvSeed', {
            tenant_id: initData.tenant.id,
            issue_id: initData.issue.id,
        });

        // Mark as in iframe
        this.$store.commit('inIframe', { inIframe: true });
    }

    /**
     * Store content structure data from Delfino.
     *
     * @param {ContentStructure} content - Data from content.getContentStructure()
     */
    storeContentStructure(content) {
        // Common data
        this.$store.commit('publicationCover', { cover: content.cover });

        // File-type specific data
        switch (content.fileType) {
            case 'pdf':
                this.storePdfContent(content);
                break;
            case 'epub':
                this.storeEpubContent(content);
                break;
            case 'audio':
                this.storeAudioContent(content);
                break;
        }
    }

    /**
     * Store PDF content structure.
     *
     * @param {PdfContentStructure} content
     */
    storePdfContent(content) {
        this.$store.commit('url', { url: content.url });
        this.$store.commit('filesUrls', { files_urls: content.files_urls });
        this.$store.commit('filesInfo', { files_info: content.files_info });
        this.$store.commit('tableOfContents', { table_of_contents: content.tableOfContents });
        this.$store.commit('setIssueArticles', { articles: content.articles });
    }

    /**
     * Store EPUB content structure.
     *
     * @param {EpubContentStructure} content
     */
    storeEpubContent(content) {
        // Ensure file path has trailing slash
        const filePath = content.filePath.replace(/\/?$/, '/');
        this.$store.commit('filePath', { filePath });
        this.$store.commit('customPreview', { customPreview: content.customPreview });
        this.$store.commit('setToc', content.tableOfContents);
        this.$store.commit('setSpineAndTotalWords', {
            spine: content.spine,
            totalWords: content.totalWords,
        });
    }

    /**
     * Store Audio content structure.
     *
     * @param {AudioContentStructure} content
     */
    storeAudioContent(content) {
        this.$store.commit('loadingAudiobookPlaylist', {
            playlist: content.tracks.map(track => ({
                chapter: track.index + 1,
                title: track.title,
                duration: secondToMMSS(track.duration),
                durationInSeconds: track.duration,
                durationInSecondsPreview: track.durationPreview,
                file: track.fileUrl,
                chunks: track.chunks ?? [],
            })),
            duration: secondToMMSS(content.totalDuration),
            chapters: content.totalChapters,
        });
    }

    /**
     * Navigate to last reading location.
     *
     * @param {SessionInitData} initData
     */
    navigateToLastLocation(initData) {
        const lastLocation = initData.lastLocation;
        if (!lastLocation || initData.modes.preview) return;

        if (this.$store.getters.isPdf) {
            this.$store.dispatch('goToPage', {
                page: lastLocation.page || 1,
            });
        } else if (this.$store.getters.isEpub) {
            // R2 locator format takes priority over legacy R1 format
            if (lastLocation.locator) {
                this.$store.dispatch('setLastLocation', {
                    locator: lastLocation.locator,
                    dontNotify: true,
                });
            } else {
                this.$store.dispatch('setLastLocation', {
                    chapterHref: lastLocation.chapterHref,
                    cfi: lastLocation.cfi,
                    dontNotify: true,
                });
            }
        } else if (this.$store.getters.isAudiobook) {
            this.$store.dispatch('setLastLocation', {
                currentSeek: lastLocation.currentSeek,
                currentTrack: lastLocation.currentTrack,
                dontNotify: true,
            });
        }
    }

    /**
     * Set Sentry tags from init data.
     *
     * @param {SessionInitData} initData
     */
    setSentryTagsFromInitData(initData) {
        try {
            Sentry.setTags({
                host: this.$store.state.volpeHost,
                issue_full_url: initData.issue.issueFullUrl,
                issue_url: initData.issue.issueFullUrl,
                issue_title: initData.issue.name,
                issue_lang: initData.issue.lang,
                issue_file_type: initData.issue.fileType,
                issue_external_id: initData.issue.externalId,
            });
        } catch (error) {
            console.error('Error setting sentry tags:', error);
        }
    }

    // ============================================================
    // IFRAME LOADING (tries Delfino first, falls back to legacy)
    // ============================================================

    // This is the loading process when Volpe is inside a Farfalla instance
    async loadInsideIframe() {
        this.eventBus.$emit('LoadingProcess.loadInsideIframe');

        if (isHostCommunicationInitialized()) {
            try {
                await this.loadViaDelfino();
                return;
            } catch (error) {
                console.warn('[LoadingProcess] Delfino loading failed:', error);
                Sentry.captureException(error);
                return;
            }
        }

        // Delfino not initialized - expected in local dev in standalone mode, but a problem in production
        if (import.meta.env.VITE_ENV === 'local' && HostBridge.isStandalone()) {
            await this.loadStandAloneDevMode();
            return;
        }

        // Production/staging without Delfino initialized - report to Sentry
        console.warn('[LoadingProcess] Delfino not initialized in production iframe');
        Sentry.captureMessage('Delfino not initialized in production iframe', 'error');
    }

    // This is the loading process when running Volpe outside a Farfalla instance
    async loadStandAloneDevMode() {
        this.eventBus.$emit('LoadingProcess.loadStandAloneDevMode');
        this.$store.commit('inIframe', {
            inIframe: false,
        });
        this.$store.commit('setSettings', {
            layout: 'single',
        });

        this.$store.dispatch('openOnboarding');

        await this.initMockPolpoSession();

        const testData = getTestData();

        // Apply all test data to store using helper function
        applyTestDataToStore(this.$store, testData);

        this.$store.dispatch('initToggleUi', {
            uiHidden: true,
        });

        if (localStorage.getItem('setTheme')) {
            this.$store.dispatch('setTheme', { theme: localStorage.getItem('setTheme') });
        }

        this.$store.dispatch('initializeThemeListener');

        if (!this.isPdf && localStorage.getItem('notifyLocationChange')) {
            this.$store.dispatch('setLastLocation', {
                ...JSON.parse(localStorage.getItem('notifyLocationChange')),
                dontNotify: true,
            });
        }

        this.loadingProcessFinished();
    }

    async initMockPolpoSession() {
        this.eventBus.$emit('LoadingProcess.initMockPolpoSession');

        this.$store.commit('polpoSessionHasBeenInitiated');
        this.$store.dispatch('fetchSelections');
        this.$store.commit('setHighlightModel', highlightModel);

        this.$store.commit('setCopyContentToClipboard', { enabled: true });
        this.$store.commit('aiActions', ['explain-test', 'expand-test', 'summarize-test']);

        if (!this.$store.state.plaPublicationManifestVersion) {
            this.$store.commit('plaPublicationManifestVersion', {
                plaPublicationManifestVersion: '1.0.0',
            });
        }
    }

    loadingProcessFinished() {
        if (this.$store.state.isLoadingProcessFinished) return;
        this.$store.commit('loadingProcessIsFinished');

        if (this.$store.state.sharedArticle.id) {
            const page =
                this.$store.state.articles[
                    findIndex(this.$store.state.articles, ['id', this.$store.state.sharedArticle.id])
                ].start_page;
            this.$store.dispatch('goToPage', {
                page,
            });
        }

        document.querySelector('html').classList.add('ready');
    }
}

export default LoadingProcess;
